import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CookieConsentService } from '@core/services/cookie-consent.service';
import { EnvironmentService } from '@core/services/environment.service';
import { Tracker } from '@core/services/event-tracker.service';
import { IdleCallbackService } from '@core/services/idle-callback.service';
import { PlausibleService } from '@core/services/plausible.service';
import { ScriptLoaderService } from '@core/services/script-loader.service';
import { WINDOW } from '@core/services/window.service';
import { AnalyticsEventName } from '@core/utils/AnalyticsEvents';
import { switchMap, take, tap } from 'rxjs/operators';

type GoogleWindow = Window & {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
};

@Injectable({
    providedIn: 'root',
})
export class GtagService implements Tracker {
    private readonly gTagManagerConfigCode = 'GTM-WM6LWCD';
    private cookieConsentService = inject(CookieConsentService);
    private environmentService = inject(EnvironmentService);
    private plausibleService = inject(PlausibleService);
    private idleCallbackService = inject(IdleCallbackService);
    private scriptLoaderService = inject(ScriptLoaderService);
    private window = inject<GoogleWindow>(WINDOW);
    private destroyRef = inject(DestroyRef);
    private gtag = this.window.gtag;

    setup() {
        if (this.environmentService.isBrowser()) {
            this.cookieConsentService.consentChanged
                .pipe(
                    tap(consent => this.updateConsent(consent)),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe();
            if (this.plausibleService.isActive()) {
                this.idleCallbackService
                    .firstIdleCallback()
                    .pipe(
                        switchMap(() => this.loadGTagManagerScript()),
                        tap(() => {
                            this.window.dataLayer.push({
                                'gtm.start': new Date().getTime(),
                                event: 'gtm.js',
                            });
                        })
                    )
                    .subscribe();
            }
        }
    }

    updateConsent(consent: boolean) {
        const status = consent ? 'granted' : 'denied';
        this.gtag('consent', 'update', {
            ad_user_data: status,
            ad_personalization: status,
            ad_storage: status,
            analytics_storage: status,
        });
    }

    trackEvent(
        name: AnalyticsEventName,
        props?: { [key: string]: string | number | boolean }
    ) {
        this.cookieConsentService.consentChanged
            .pipe(take(1))
            .subscribe(consentGiven => {
                if (consentGiven && this.plausibleService.isActive()) {
                    this.window.dataLayer.push({
                        ...props,
                        event: name,
                    });
                }
            });
    }

    loadGTagManagerScript() {
        let gtmScriptURL = `https://www.googletagmanager.com/gtm.js?id=${this.gTagManagerConfigCode}`;

        if (this.environmentService.isStaging()) {
            gtmScriptURL = `${gtmScriptURL}&gtm_auth=eEHmNsduKuXqcYOyiFcjpw&gtm_preview=env-21&gtm_cookies_win=x`;
        }
        return this.scriptLoaderService.load(gtmScriptURL);
    }
}
